import { ReactNode } from 'react';
import Image from 'next/image';

import { Box, Card, Heading, List, ListItem, ResponsiveValue, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SanityImage } from '~/components/sanity-image';
import type { CompanyContent } from '~/features/companies/models/company';

const DEFAULT_BG_GRADIENT = 'linear(180deg, #20003A 0%, #6100B0 100%)';
const DEFAULT_TEXTURE_SRC = '/images/experiments/ab_plp_redesign_2025/purple-stripes-diagonal.png';
const DEFAULT_BORDER_COLOR = '#6100B0';

const DEFAULT_INDUSTRY_LEADERS_COPY = {
  title: 'Curriculum developed by the best',
  description: "Learn from instructors who've worked at tech's top companies.",
  companyNames: ['Google', 'Microsoft', 'Nvidia', 'Intel', 'Dell'],
} as const;

/**
 * Only the light (white) lo-fi mark reads on the section's dark gradient
 * background. Companies without one are skipped rather than falling back to a
 * dark or hi-fi logo that would be invisible on the dark surface.
 */
function pickIndustryLeaderLogo(company: CompanyContent) {
  return company.lofiLogoLight;
}

type IndustryLeaderCompany = StaticExperienceIndustryLeadersSectionProps['companies'][number];

export type IndustryLeaderCompanyWithLogo = IndustryLeaderCompany & {
  logo: NonNullable<IndustryLeaderCompany['logo']>;
};

const DEFAULT_LOGO_HEIGHT = { base: '20px', md: '30px' };

export function IndustryLeaderLogo({
  company,
  height = DEFAULT_LOGO_HEIGHT,
}: {
  company: IndustryLeaderCompanyWithLogo;
  height?: ResponsiveValue<string>;
}) {
  return (
    <Box height={height}>
      <SanityImage
        alt={company.name}
        image={company.logo}
        style={{
          height: '100%',
          width: 'auto',
        }}
      />
    </Box>
  );
}

export type ExperienceIndustryLeadersSectionProps = StaticExperienceIndustryLeadersSectionProps & {
  bgGradient?: string;
  textureSrc?: string;
  borderColor?: string;
  descriptionColor?: string;
  /** Overrides how each logo is rendered, e.g. to size a single logo differently. Defaults to `IndustryLeaderLogo`. */
  renderLogo?: (company: IndustryLeaderCompanyWithLogo) => ReactNode;
};

export function ExperienceIndustryLeadersSection({
  title,
  description,
  companies,
  bgGradient = DEFAULT_BG_GRADIENT,
  textureSrc = DEFAULT_TEXTURE_SRC,
  borderColor = DEFAULT_BORDER_COLOR,
  descriptionColor,
  renderLogo = (company) => <IndustryLeaderLogo company={company} />,
}: ExperienceIndustryLeadersSectionProps) {
  return (
    <ContentContainer contentMaxWidth='content-max-width-lg'>
      <Card
        as='section'
        bgGradient={bgGradient}
        border='none'
        color='white'
        display='flex'
        flexDirection='column'
        overflow='hidden'
        position='relative'
        borderRadius={{
          base: '0px',
          md: '12px',
        }}
        gap={{
          base: 6,
          md: 12,
        }}
        mx={{
          base: '-16px',
          md: '0px',
        }}
        padding={{
          base: '32px 16px',
          md: '56px',
        }}
      >
        <Box inset={0} position='absolute'>
          <Image
            alt=''
            loading='lazy'
            quality={75}
            src={textureSrc}
            style={{
              pointerEvents: 'none',
              objectFit: 'contain',
              objectPosition: 'right bottom',
            }}
            fill
          />
        </Box>
        <Box position='relative' textAlign='center'>
          <Heading
            as='h2'
            fontWeight='600'
            mb={4}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.14',
              md: '1.2',
            }}
          >
            {title}
          </Heading>
          <Text color={descriptionColor}>{description}</Text>
        </Box>
        <List
          alignItems='center'
          borderColor={borderColor}
          borderTop='1px solid'
          display='flex'
          flexWrap='wrap'
          gap={8}
          position='relative'
          justifyContent={{
            base: 'center',
            md: 'space-around',
          }}
          pt={{
            base: 6,
            md: 12,
          }}
        >
          {companies.map((company) => {
            const { logo } = company;

            // Skip companies with no white logo rather than rendering an empty slot.
            if (!logo) {
              return null;
            }

            return <ListItem key={company.name}>{renderLogo({ ...company, logo })}</ListItem>;
          })}
        </List>
      </Card>
    </ContentContainer>
  );
}

export async function transformStaticExperienceIndustryLeadersSectionProps(options?: {
  title?: string;
  description?: string;
  companyNames?: string[];
}) {
  const title = options?.title ?? DEFAULT_INDUSTRY_LEADERS_COPY.title;
  const description = options?.description ?? DEFAULT_INDUSTRY_LEADERS_COPY.description;
  const companyNames = options?.companyNames ?? [...DEFAULT_INDUSTRY_LEADERS_COPY.companyNames];

  const { getCompanies } = await import('~/features/companies/controllers/get-companies');
  const companies = await getCompanies(companyNames).then((resolvedCompanies) =>
    resolvedCompanies
      .map((company) => ({
        name: company.name,
        logo: pickIndustryLeaderLogo(company),
      }))
      .sort((a, b) => companyNames.indexOf(a.name) - companyNames.indexOf(b.name))
  );

  return {
    title,
    description,
    companies,
  };
}

export type StaticExperienceIndustryLeadersSectionProps = Awaited<
  ReturnType<typeof transformStaticExperienceIndustryLeadersSectionProps>
>;
